import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flag } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { flagApi, ticketPath, FLAG_STATUS_STYLES } from './flagUtils';

const POPUP_STATUSES = ['Flagged', 'In Progress', 'Returned for Correction'];
const POLL_MS = 60000;
// Shown-this-sign-in memory, keyed to the session token so each new sign-in pops open flags again.
const seenKey = () => `myhelp_flag_alerts_${(localStorage.getItem('myhelp_token') || '').slice(-24)}`;
const stage = (f) => (f.forReview ? `submitted-${f.submitted_at}` : f.status === 'Returned for Correction' ? `returned-${f.returned_at}` : 'open');
const TITLES = { review: 'Caseload Review corrections ready for your approval', returned: 'Caseload Review returned for correction', open: 'Caseload Review flagged for correction' };
const getSeen = () => { try { return JSON.parse(sessionStorage.getItem(seenKey()) || '{}'); } catch { return {}; } };
const otherDialogOpen = () => !!document.querySelector('[role="dialog"], [role="alertdialog"]');

const HeadsUp = ({ flag, onOpenTicket, onLater }) => (
  <Dialog open onOpenChange={(o) => !o && onLater()}>
    <DialogContent className="max-w-md" data-testid="flag-heads-up">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Flag className="h-5 w-5 text-red-500" />
          {TITLES[flag.forReview ? 'review' : flag.status === 'Returned for Correction' ? 'returned' : 'open']}</DialogTitle>
        <DialogDescription>{flag.forReview ? <>{flag.assignee_name || flag.case_manager_name} submitted corrections for <b>{flag.participant_name}</b> (week of {flag.week_of_date}).</> : <>{flag.status === 'Returned for Correction' ? (flag.returned_by_name || 'Management') + ' returned' : flag.flagged_by_name + ' flagged'} the review for <b>{flag.participant_name}</b> (week of {flag.week_of_date}).</>}</DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Badge variant="outline" className={FLAG_STATUS_STYLES[flag.status]} data-testid="flag-heads-up-status">{flag.status}</Badge>
        <p className="text-sm text-muted-foreground line-clamp-3">&ldquo;{flag.forReview ? (flag.follow_up_summary?.text || flag.comments) : (flag.status === 'Returned for Correction' && flag.return_comment) || flag.comments}&rdquo;</p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onLater} data-testid="flag-heads-up-later-btn">Later</Button>
        <Button onClick={onOpenTicket} data-testid="flag-heads-up-open-btn">Open Ticket</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const FlagAlertWatcher = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const queue = useRef([]);
  const currentRef = useRef(null);

  // Side effects (queue.shift) must stay outside the state updater: StrictMode double-invokes updaters and would drop the flag.
  const showNext = useCallback(() => {
    if (currentRef.current || !queue.current.length || otherDialogOpen()) return;
    currentRef.current = queue.current.shift();
    setCurrent(currentRef.current);
  }, []);

  const check = useCallback(async () => {
    if (!user) return;
    try {
      const seen = getSeen();
      const [mine, review] = await Promise.all([flagApi.mine(), flagApi.awaiting()]);
      const all = [...mine.filter((f) => POPUP_STATUSES.includes(f.status)), ...review.map((f) => ({ ...f, forReview: true }))];
      const fresh = all.filter((f) => !(seen[f.id] || []).includes(stage(f)));
      fresh.forEach((f) => { seen[f.id] = [...(seen[f.id] || []), stage(f)]; });
      sessionStorage.setItem(seenKey(), JSON.stringify(seen));
      queue.current.push(...fresh.filter((f) => !queue.current.some((q) => q.id === f.id && !!q.forReview === !!f.forReview)));
      showNext();
    } catch { /* alerts never block the app */ }
  }, [user, showNext]);

  useEffect(() => {
    if (!user) return undefined;
    const first = setTimeout(check, 2500);
    const poll = setInterval(check, POLL_MS);
    const waitForOtherDialogs = setInterval(showNext, 2000);
    return () => { clearTimeout(first); clearInterval(poll); clearInterval(waitForOtherDialogs); };
  }, [user, check, showNext]);

  if (!current) return null;
  const done = () => { currentRef.current = null; setCurrent(null); };
  return <HeadsUp flag={current} onLater={done} onOpenTicket={() => { done(); navigate(ticketPath(current.id)); }} />;
};
